import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';

import { deactivateEnable } from '.';
import { ErrorService } from '../../core/error/error.service';
import { NavigationConfirm } from '../../core/guards/deactive.guard';
import { ContextData } from '../../core/models/context-data';
import { MenuInfo } from '../../core/models/menu-info';
import { UserInfoService } from '../../core/services';
import { HintDialogService } from '../../core/services/hint-dialog.service';
import { LayoutService } from '../../core/services/layout.service';
import { RouteStateService } from '../../core/services/route-state.service';
import {
  EnhancedFormControl,
  FormGroupEnhanced,
  IForm,
} from '../../models/rt-common';
import { RoleEnum } from '../../rt-shared/enums/rbac-role.enum';
import {
  GetRoleCodeService,
  RetailDropdownOptionsService,
  UserTraceLogService,
} from '../../rt-shared/services';
import { AuditInfoService } from '../../rt-shared/services/auditInfo.service';
import { AppError } from '../../rt-shared/services/error/models/app-error';
import { LoaderService } from '../../rt-shared/ui/loader/loader.service';
import { ToastService } from '../../rt-shared/ui/toast/toast.service';
import { BaseComponent } from './base.component';
import { DataTableComponent } from './data-table/data-table.component';

/**
 * 基底頁面
 *
 * @export
 * @abstract
 * @class BasePageComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 */
@Component({
  template: '',
})
export abstract class BaseSubComponent
  extends BaseComponent
  implements OnInit, OnDestroy, NavigationConfirm
{
  routeStateService: RouteStateService;
  toastService: ToastService;
  loaderService: LoaderService;
  hintDialogService: HintDialogService;
  layoutService: LayoutService;
  errorService: ErrorService;
  route: ActivatedRoute;
  routeData: any;
  cacheData: any;
  fromMenuEntry = false;
  fromTodo = false;
  breadcrumbItems: MenuInfo[] = [];
  contextData: ContextData;
  _auditInfoService: AuditInfoService;
  _userTraceLogService: UserTraceLogService;
  _retailDropdownOptionsService: RetailDropdownOptionsService;
  _getRoleCodeService: GetRoleCodeService;
  _userInfoService: UserInfoService;
  deactivateEnable = new FormGroupEnhanced<IForm<deactivateEnable>>({
    datatableEnable: new EnhancedFormControl('', false),
  });
  RoleEnum = RoleEnum;
  constructor(injector: Injector) {
    super(injector);
    this.routeStateService = injector.get<RouteStateService>(RouteStateService);
    this.toastService = injector.get<ToastService>(ToastService);
    this.loaderService = injector.get<LoaderService>(LoaderService);
    this.hintDialogService = injector.get<HintDialogService>(HintDialogService);
    this.layoutService = injector.get<LayoutService>(LayoutService);
    this.errorService = injector.get<ErrorService>(ErrorService);
    this.route = injector.get<ActivatedRoute>(ActivatedRoute);
    this.contextData = injector.get<ContextData>(ContextData);
    this._auditInfoService = injector.get<AuditInfoService>(AuditInfoService);
    this._userTraceLogService = injector.get(UserTraceLogService);
    this._retailDropdownOptionsService = injector.get(
      RetailDropdownOptionsService
    );
    this._getRoleCodeService = injector.get(GetRoleCodeService);
    this._userInfoService = injector.get(UserInfoService);
  }
  @ViewChild('dataTable') dataTable!: DataTableComponent;

  get userTraceLogService() {
    return this._userTraceLogService;
  }
  get auditInfoService() {
    return this._auditInfoService;
  }
  get isOpenWindow() {
    return this.routeData?.isOpenWindow;
  }

  ngOnInit(): void {
    try {
      this.initPageRouteData();
      this.logger.info('Context data: ', this.contextData.getValue());
      this.initDefaultData();
    } catch (e) {
      const useError = new AppError();
      useError.error = e;
      this.errorService.handleUnknownError(useError);
    }
  }

  ngOnDestroy(): void {}

  /**
   * 初始化路由資料
   *
   * @memberof BasePageComponent
   */
  initPageRouteData(): void {
    const stateData = this.routeStateService.state?.routeState?.data;
    const storageData = this.routeStateService.getCurrent()?.data ?? {};
    let queryData = {} as Record<string, any>;
    const pathData = this.route.snapshot.params ?? {};
    if (this.route.snapshot.queryParams)
      Object.entries(this.route.snapshot.queryParams).forEach((entry) => {
        const key = entry[0];
        let value = entry[1];
        if (entry[1].includes('{') && entry[1].includes('}')) {
          try {
            value = JSON.parse(entry[1]);
          } catch (error) {
            value = entry[1];
          }
        }
        else if (['true', 'false'].includes(entry[1])) {
          value = Boolean(entry[1]);
        }
        queryData[key] = value;
      });
    this.routeData = {
      ...(stateData ?? storageData),
      ...queryData,
      ...pathData,
    };
    this.logger.info(
      'Route data: ',
      this.routeStateService.state,
      this.routeStateService.getCurrent(),
      this.route.snapshot,
      this.routeData
    );
    this.logger.info('Cache data: ', this.cacheData);
  }

  /**
   * 初始化資料
   *
   * @abstract
   * @memberof BasePageComponent
   */
  abstract initDefaultData(): void;

  canDeactivate(): Observable<boolean> {
    //判斷deactivateEnable裡是否有元件開關為true
    for (const field in this.deactivateEnable.controls) {
      const control = this.deactivateEnable.get(field);
      this.logger.debug('deactivate', control?.getRawValue());
      if (control?.getRawValue() == true) {
        if (this.dataTable.isTableEditing == true) {
          let observable = new Observable<boolean>((observer) => {
            // 跳確認視窗
            this.hintDialogService.showExitWithoutSavingHint(
              () => {
                observer.next(true);
              },
              () => {
                observer.next(false);
              }
            );
          });
          return observable;
        }
      }
    }
    return of(true);
  }

  routeToNestPage(url: string, data?: { [key: string]: any }): void {
    const routeData = {
      ...(data ?? {}),
      parentBreadcrumb: this.breadcrumbItems,
    };
    this.routeStateService.navigateTo(url, routeData);
  }
}
